import { useCallback, useMemo, useRef } from 'react';
import type {
  CellContextMenuEvent,
  ColDef,
  GridOptions,
  PaginationChangedEvent,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

type AppDataGridProps<TRow extends object> = {
  rowData: TRow[];
  columnDefs: ColDef<TRow>[];
  quickFilterText?: string;
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  height?: number;
  className?: string;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<TRow>) => void;
  onCellContextMenu?: (event: CellContextMenuEvent<TRow>) => void;
  onPageSizeChange?: (pageSize: number) => void;
  suppressBrowserContextMenu?: boolean;
};

export function AppDataGrid<TRow extends object>({
  rowData,
  columnDefs,
  quickFilterText,
  pagination = true,
  pageSize = 10,
  pageSizeOptions,
  height = 460,
  className,
  onRowDoubleClicked,
  onCellContextMenu,
  onPageSizeChange,
  suppressBrowserContextMenu = false,
}: AppDataGridProps<TRow>) {
  const lastReportedPageSizeRef = useRef<number>(pageSize);
  const defaultColDef = useMemo<ColDef<TRow>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 120,
    }),
    [],
  );

  const gridOptions = useMemo<GridOptions<TRow>>(
    () => ({
      animateRows: true,
      suppressCellFocus: true,
      suppressMovableColumns: true,
      rowHeight: 58,
      headerHeight: 46,
    }),
    [],
  );

  const paginationPageSizeSelector = useMemo(() => {
    const baseOptions = pageSizeOptions ?? [10, 25, 50, 100, 200];
    return [...new Set([...baseOptions, pageSize])].sort((left, right) => left - right);
  }, [pageSize, pageSizeOptions]);

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<TRow>) => {
      if (!onPageSizeChange) {
        return;
      }

      const currentPageSize = event.api.paginationGetPageSize();

      if (currentPageSize !== lastReportedPageSizeRef.current) {
        lastReportedPageSizeRef.current = currentPageSize;
        onPageSizeChange(currentPageSize);
      }
    },
    [onPageSizeChange],
  );

  return (
    <div
      className={`ag-theme-quartz app-data-grid ${className ?? ''}`}
      style={{ height }}
      onContextMenu={
        suppressBrowserContextMenu
          ? (event) => {
              event.preventDefault();
            }
          : undefined
      }
    >
      <AgGridReact<TRow>
        rowData={rowData}
        columnDefs={columnDefs}
        theme="legacy"
        defaultColDef={defaultColDef}
        quickFilterText={quickFilterText}
        pagination={pagination}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        gridOptions={gridOptions}
        onRowDoubleClicked={onRowDoubleClicked}
        onCellContextMenu={onCellContextMenu}
        onPaginationChanged={handlePaginationChanged}
      />
    </div>
  );
}
